/**
 * Krok MOB1 — ZMENSENI FOTKY V PROHLIZECI pred odeslanim.
 *
 * Prevzato z RedHand Mobil (`report-problem/resize-image.ts`) a rozsireno o
 * druhy vystup. JEDNA implementace pro obe mista, ktera fotku posilaji:
 *  - „Nahlásit problém" potrebuje base64 do tela pozadavku (priloha tiketu),
 *  - „Vyfotit" u nemovitosti potrebuje `File` pro stavajici upload API.
 * Dve kopie teze matematiky by se rozesly (jedna by zapomnela na pomer stran).
 *
 * Bez knihoven — `createImageBitmap` + `canvas`. Fotka z telefonu ma dnes
 * bezne 12 Mpx a 5 MB; na mobilnim pripojeni by nahrani trvalo minuty a
 * server by stejne delal nahledy.
 */

export interface PreparedImage {
  readonly name: string;
  readonly contentType: string;
  readonly dataBase64: string;
}

export interface ResizeOptions {
  /** Delsi strana vysledku v px. */
  readonly maxDimension?: number;
  /** Kvalita JPEG 0–1. */
  readonly quality?: number;
}

/** Priloha hlaseni — staci na cteni obrazovky, ne na tisk. */
export const SCREENSHOT_RESIZE: Required<ResizeOptions> = { maxDimension: 1600, quality: 0.8 };
/** Fotka nemovitosti — vetsi, pouziva se i v inzeratu a reportu. */
export const PHOTO_RESIZE: Required<ResizeOptions> = { maxDimension: 2560, quality: 0.85 };

function jpegName(name: string): string {
  return `${name.replace(/\.\w+$/, '')}.jpg`;
}

async function toJpegBlob(file: File, options: Required<ResizeOptions>): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  let width = bitmap.width;
  let height = bitmap.height;
  if (width > options.maxDimension || height > options.maxDimension) {
    const scale = options.maxDimension / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas neni podporovany');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) reject(new Error('převod obrázku selhal'));
        else resolve(blob);
      },
      'image/jpeg',
      options.quality,
    );
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('neplatný obsah souboru'));
        return;
      }
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('čtení souboru selhalo'));
    };
    reader.readAsDataURL(blob);
  });
}

/** Zmensena fotka jako `File` pro stavajici upload API. */
export async function resizeImageToJpegFile(
  file: File,
  options: ResizeOptions = PHOTO_RESIZE,
): Promise<File> {
  const settings = { ...PHOTO_RESIZE, ...options };
  const blob = await toJpegBlob(file, settings);
  return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
}

/** Zmensena fotka jako base64 pro telo pozadavku (priloha hlaseni). */
export async function resizeImageToJpegBase64(
  file: File,
  options: ResizeOptions = SCREENSHOT_RESIZE,
): Promise<PreparedImage> {
  const settings = { ...SCREENSHOT_RESIZE, ...options };
  const blob = await toJpegBlob(file, settings);
  return {
    name: jpegName(file.name),
    contentType: 'image/jpeg',
    dataBase64: await blobToBase64(blob),
  };
}

/**
 * Zmenseni, ktere NIKDY neshodi nahravani: kdyz prohlizec formatu nerozumi
 * (HEIC ze starsiho iPhonu, poskozeny soubor), jde nahoru puvodni soubor.
 * Ztratit fotku z obhlidky kvuli kosmetice by bylo horsi nez vetsi prenos.
 */
export async function resizeImageOrOriginal(
  file: File,
  options: ResizeOptions = PHOTO_RESIZE,
): Promise<File> {
  try {
    return await resizeImageToJpegFile(file, options);
  } catch {
    return file;
  }
}

/**
 * Krok NAP3 bod 2 (A27) — PORTRET MAKLERE: orez na CTVEREC a `data:` URI.
 *
 * Tataz matematika (`createImageBitmap` + canvas) jako zmenseni fotky vys —
 * jen jiny vystup, takze druha kopie prevodu obrazku v adminu nevznika.
 * Ctverec: z fotky se bere stredovy vyrez kratsi strany (portret na sirku i
 * na vysku tak zustane obliceje, ne pruh zdi) a zmensi na `size` px.
 *
 * Vystup je JPEG v `data:` URI — tentyz tvar, v jakem se uklada logo
 * kancelare (krok FAK4) a jaky prijima `PATCH /api/me/portrait`.
 */
export const PORTRAIT_SIZE_PX = 512;
export const PORTRAIT_QUALITY = 0.82;

export async function cropToSquareDataUrl(
  file: File,
  size = PORTRAIT_SIZE_PX,
  quality = PORTRAIT_QUALITY,
): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas neni podporovany');
  ctx.drawImage(
    bitmap,
    Math.round((bitmap.width - side) / 2),
    Math.round((bitmap.height - side) / 2),
    side,
    side,
    0,
    0,
    size,
    size,
  );
  return canvas.toDataURL('image/jpeg', quality);
}
